import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Avatar,
  Card,
  CardContent,
  Autocomplete,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { 
  Refresh, 
  Add, 
  Delete, 
  Inventory,
  AttachMoney,
  ShoppingBag,
  Search,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListBundlesQuery,
  useAddBundleMutation,
  useDeleteBundleMutation,
  Bundle as BundleType,
} from '../../features/admin/bundleApi.ts';

import {
  useListProductsQuery,
  Product,
} from '../../features/admin/productApi.ts';

interface BundleFormData {
  product_id: string;
  prod_name: string;
  prod_desc: string;
  product_retail_price: string;
  product_unit: string;
  product_quantity: string;
  product_stock: string;
  active: boolean;
}

const Bundle = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [productSearchStr, setProductSearchStr] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<BundleFormData>({
    product_id: '',
    prod_name: '',
    prod_desc: '',
    product_retail_price: '',
    product_unit: 'pcs',
    product_quantity: '1',
    product_stock: '0',
    active: true,
  });
  const [formErrors, setFormErrors] = useState<Partial<BundleFormData>>({});

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListBundlesQuery({});

  const {
    data: productsResponse,
    isLoading: isLoadingProducts,
  } = useListProductsQuery({
    page: 1,
    limit: 50,
    search_str: productSearchStr
  });

  const [addBundle, { isLoading: isAdding }] = useAddBundleMutation();
  const [deleteBundle, { isLoading: isDeleting }] = useDeleteBundleMutation();

  // Handle API response structure
  const bundles = data?.data || [];
  const products = productsResponse?.result || productsResponse?.data || [];
  const totalRecords = bundles.length;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
    setSelectedProduct(null);
    setProductSearchStr('');
    setFormData({
      product_id: '',
      prod_name: '',
      prod_desc: '',
      product_retail_price: '',
      product_unit: 'pcs',
      product_quantity: '1',
      product_stock: '0',
      active: true,
    });
    setFormErrors({});
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedProduct(null);
    setProductSearchStr('');
    setFormData({
      product_id: '',
      prod_name: '',
      prod_desc: '',
      product_retail_price: '',
      product_unit: 'pcs',
      product_quantity: '1',
      product_stock: '0',
      active: true,
    });
    setFormErrors({});
  };

  const handleProductSelect = (event: any, value: Product | null) => {
    setSelectedProduct(value);
    if (value) {
      setFormData(prev => ({
        ...prev,
        product_id: (value.product_id || value.id || '').toString(),
        prod_name: value.prod_name || '',
        prod_desc: value.prod_desc || '',
        product_retail_price: (value.product_price || '').toString(),
        product_unit: value.product_unit || 'pcs',
      }));
      // Clear product_id error when product is selected
      if (formErrors.product_id) {
        setFormErrors(prev => ({
          ...prev,
          product_id: undefined
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        product_id: '',
        prod_name: '',
        prod_desc: '',
        product_retail_price: '',
        product_unit: 'pcs',
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (formErrors[name as keyof BundleFormData]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleActiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      active: e.target.checked
    }));
  };

  const validateForm = (): boolean => {
    const errors: Partial<BundleFormData> = {};

    if (!formData.product_id.trim()) {
      errors.product_id = 'Product is required';
    }
    if (!formData.prod_name.trim()) {
      errors.prod_name = 'Product name is required';
    }
    if (!formData.prod_desc.trim()) {
      errors.prod_desc = 'Description is required';
    }
    if (!formData.product_retail_price.trim()) {
      errors.product_retail_price = 'Price is required';
    } else if (isNaN(Number(formData.product_retail_price)) || Number(formData.product_retail_price) <= 0) {
      errors.product_retail_price = 'Price must be a positive number';
    }
    if (!formData.product_quantity.trim()) {
      errors.product_quantity = 'Quantity is required';
    } else if (isNaN(Number(formData.product_quantity)) || Number(formData.product_quantity) <= 0) {
      errors.product_quantity = 'Quantity must be a positive number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddBundle = async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedProduct) {
      return;
    }

    try {
      const payload = {
        product_id: Number(formData.product_id),
        prod_name: formData.prod_name,
        prod_desc: formData.prod_desc,
        product_retail_price: formData.product_retail_price,
        product_unit: formData.product_unit,
        product_quantity: Number(formData.product_quantity),
        product_stock: Number(formData.product_stock),
        active: formData.active ? 1 : 0,
      };

      await addBundle(payload).unwrap();
      handleCloseAddDialog();
      // Show success message (you can add a snackbar here)
    } catch (err) {
      console.error('Failed to add bundle:', err);
      // Show error message (you can add a snackbar here)
    }
  };

  const handleDelete = async (bundle_id: number) => {
    if (window.confirm('Are you sure you want to delete this bundle?')) {
      try {
        await deleteBundle(bundle_id).unwrap();
        // Show success message
      } catch (err) {
        console.error('Failed to delete bundle:', err);
        // Show error message
      }
    }
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'Access denied. Please verify your token and permissions.';
        case 401:
          return 'Unauthorized. Please login again.';
        case 404:
          return 'Bundles not found.';
        case 500:
          return 'Server error. Please try again later.';
        default:
          return 'Failed to load bundles. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: 'AED',
      currencyDisplay: 'symbol'
    }).format(numAmount);
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) {
      return { label: 'Out of Stock', color: '#f44336' };
    } else if (stock < 5) {
      return { label: 'Low Stock', color: '#ff9800' };
    }
    return { label: 'In Stock', color: '#4caf50' };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for large screens */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              p: 2,
              backgroundColor: '#f5f5f5',
            }}
          >
            {/* Header Section */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3
            }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Bundle Management
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' }
              }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAddDialog}
                  size="small"
                  sx={{ backgroundColor: '#1976d2' }}
                >
                  Add Bundle
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={isLoading}
                  size="small"
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total {totalRecords} bundle{totalRecords === 1 ? '' : 's'}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading bundles...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  <IconButton color="inherit" size="small" onClick={() => refetch()}>
                    <Refresh />
                  </IconButton>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && bundles.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Inventory sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No bundles available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Create your first bundle to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAddDialog}
                >
                  Add Bundle
                </Button>
              </Box>
            )}

            {/* Desktop Table */}
            {!isMobile && bundles.length > 0 && !isLoading && (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="small" aria-label="bundles table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>No</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bundles.map((bundle: BundleType, index: number) => {
                      const stockStatus = getStockStatus(bundle.product_stock);
                      return (
                        <TableRow 
                          key={bundle.id}
                          sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={bundle.product_main_image ? `https://fftestservice.sarsetech.com/images/${bundle.product_main_image}` : undefined}
                                variant="rounded"
                                sx={{ width: 40, height: 40 }}
                              >
                                {bundle.prod_name?.[0] || 'B'}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {bundle.prod_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={bundle.id} 
                              size="small"
                              sx={{ 
                                backgroundColor: '#e3f2fd',
                                color: '#1565c0',
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                              {bundle.prod_desc.length > 50 ? `${bundle.prod_desc.substring(0, 50)}...` : bundle.prod_desc}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                              {formatCurrency(bundle.product_retail_price)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={bundle.product_unit} 
                              size="small"
                              sx={{ backgroundColor: '#f5f5f5' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {bundle.product_quantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${bundle.product_stock} (${stockStatus.label})`}
                              size="small"
                              sx={{ 
                                backgroundColor: stockStatus.color,
                                color: '#fff'
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(bundle.id)}
                              size="small"
                              title="Delete bundle"
                              disabled={isDeleting}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Mobile Cards */}
            {isMobile && bundles.length > 0 && !isLoading && (
              <Box>
                {bundles.map((bundle: BundleType, index: number) => {
                  const stockStatus = getStockStatus(bundle.product_stock);
                  return (
                    <Card key={bundle.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary">
                              #{index + 1} (ID: {bundle.id})
                            </Typography>
                          </Box>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(bundle.id)}
                            size="small"
                            disabled={isDeleting}
                          >
                            <Delete />
                          </IconButton>
                        </Box>

                        {/* Product Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                          <Avatar
                            src={bundle.product_main_image ? `https://fftestservice.sarsetech.com/images/${bundle.product_main_image}` : undefined}
                            variant="rounded"
                            sx={{ width: 60, height: 60 }}
                          >
                            {bundle.prod_name?.[0] || 'B'}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {bundle.prod_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {bundle.prod_desc}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Details */}
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AttachMoney fontSize="small" color="success" />
                          <Typography variant="caption" color="text.secondary">
                            Price:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            {formatCurrency(bundle.product_retail_price)}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ShoppingBag fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Unit:
                          </Typography>
                          <Chip label={bundle.product_unit} size="small" />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Qty:
                          </Typography>
                          <Typography variant="body2">{bundle.product_quantity}</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Inventory fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            Stock:
                          </Typography>
                          <Chip 
                            label={`${bundle.product_stock} (${stockStatus.label})`}
                            size="small"
                            sx={{ 
                              backgroundColor: stockStatus.color,
                              color: '#fff'
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add Bundle Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Bundle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Product Search & Select */}
            <Autocomplete
              options={products}
              getOptionLabel={(option) => `${option.prod_name} (ID: ${option.product_id || option.id})`}
              value={selectedProduct}
              onChange={handleProductSelect}
              onInputChange={(event, newInputValue) => {
                setProductSearchStr(newInputValue);
              }}
              loading={isLoadingProducts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Product *"
                  error={!!formErrors.product_id}
                  helperText={formErrors.product_id || 'Type to search for products'}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {isLoadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Avatar
                    src={option.product_main_image ? `https://fftestservice.sarsetech.com/images/${option.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 40, height: 40, mr: 2 }}
                  >
                    {option.prod_short_name?.[0] || option.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2">{option.prod_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.product_id || option.id} | Price: {formatCurrency(option.product_price || 0)}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No products found. Try a different search term."
            />

            {/* Show selected Product details */}
            {selectedProduct && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Selected Product Details:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Avatar
                    src={selectedProduct.product_main_image ? `https://fftestservice.sarsetech.com/images/${selectedProduct.product_main_image}` : undefined}
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {selectedProduct.prod_short_name?.[0] || selectedProduct.prod_name?.[0] || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {selectedProduct.prod_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Product Price: {formatCurrency(selectedProduct.product_price || 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            
            <TextField
              label="Product Name *"
              name="prod_name"
              fullWidth
              value={formData.prod_name}
              onChange={handleInputChange}
              error={!!formErrors.prod_name}
              helperText={formErrors.prod_name}
              size="small"
              disabled={!!selectedProduct}
            />
            
            <TextField
              label="Description *"
              name="prod_desc"
              fullWidth
              multiline
              rows={3}
              value={formData.prod_desc}
              onChange={handleInputChange}
              error={!!formErrors.prod_desc}
              helperText={formErrors.prod_desc}
              size="small"
              disabled={!!selectedProduct}
            />
            
            <TextField
              label="Retail Price (AED) *"
              name="product_retail_price"
              type="number"
              fullWidth
              value={formData.product_retail_price}
              onChange={handleInputChange}
              error={!!formErrors.product_retail_price}
              helperText={formErrors.product_retail_price}
              size="small"
              InputProps={{
                inputProps: { min: 0, step: 0.01 }
              }}
              disabled={!!selectedProduct}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Unit *"
                name="product_unit"
                fullWidth
                value={formData.product_unit}
                onChange={handleInputChange}
                size="small"
                disabled={!!selectedProduct}
              />
              
              <TextField
                label="Quantity *"
                name="product_quantity"
                type="number"
                fullWidth
                value={formData.product_quantity}
                onChange={handleInputChange}
                error={!!formErrors.product_quantity}
                helperText={formErrors.product_quantity}
                size="small"
                InputProps={{
                  inputProps: { min: 1, step: 1 }
                }}
              />
            </Box>

            <TextField
              label="Stock"
              name="product_stock"
              type="number"
              fullWidth
              value={formData.product_stock}
              onChange={handleInputChange}
              size="small"
              InputProps={{
                inputProps: { min: 0, step: 1 }
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleActiveChange}
                  color="primary"
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isAdding}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddBundle} 
            variant="contained"
            disabled={isAdding || !selectedProduct}
          >
            {isAdding ? 'Adding...' : 'Add Bundle'}
          </Button>
           <Button onClick={handleCloseAddDialog} disabled={isAdding}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bundle;